import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart3, ChevronDown, ChevronRight, AlertTriangle,
  CheckCircle2, RefreshCw, Shuffle, BookOpen, Info,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { aiqApi, type AiqPoolAnalysisResponse, type AiqCurriculumPoolInfo, type AiqSimulateSelectionResponse, type AiqSelectedQuizInfo } from '@/api/aiq'
import { cn } from '@/lib/utils'

const DIFFICULTY_LABELS: Record<number, string> = {
  1: 'Lv.1', 2: 'Lv.2', 3: 'Lv.3', 4: 'Lv.4', 5: 'Lv.5',
}

const DIFFICULTY_COLORS: Record<number, { bg: string; text: string; label: string }> = {
  1: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Lv.1' },
  2: { bg: 'bg-sky-100', text: 'text-sky-700', label: 'Lv.2' },
  3: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Lv.3' },
  4: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Lv.4' },
  5: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Lv.5' },
}

function DifficultyBadge({ level, count }: { level: number; count: number }) {
  const { t } = useTranslation('learner')
  const color = DIFFICULTY_COLORS[level] ?? { bg: 'bg-gray-100', text: 'text-gray-700', label: `Lv.${level}` }
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', color.bg, color.text)}>
      {color.label}: {t('learning.aiqAnalysis.countItems', { count })}
    </span>
  )
}

function DifficultyBar({ byDifficulty, total }: { byDifficulty: Record<number, number>; total: number }) {
  if (total === 0) return <div className="w-full h-3 bg-gray-100 rounded-full" />
  const levels = [1, 2, 3, 4, 5].filter(l => (byDifficulty[l] ?? 0) > 0)
  const barColors: Record<number, string> = {
    1: 'bg-emerald-400', 2: 'bg-sky-400', 3: 'bg-amber-400', 4: 'bg-orange-400', 5: 'bg-rose-400',
  }
  return (
    <div className="flex w-full h-3 overflow-hidden rounded-full">
      {levels.map(level => {
        const count = byDifficulty[level] ?? 0
        const pct = (count / total) * 100
        return (
          <div
            key={level}
            className={cn(barColors[level] ?? 'bg-gray-400')}
            style={{ width: `${pct}%` }}
            title={`Lv.${level}: ${count}개 (${pct.toFixed(0)}%)`}
          />
        )
      })}
    </div>
  )
}

function SelectionRuleCard({ rule }: { rule: AiqPoolAnalysisResponse['selectionRule'] }) {
  const { t } = useTranslation('learner')
  return (
    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
      <div className="flex gap-2 items-center mb-2">
        <Info className="w-4 h-4 text-indigo-600" />
        <span className="text-sm font-semibold text-indigo-800">{t('learning.aiqAnalysis.selectionRule')}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm text-indigo-700">
        <div>
          <span className="text-xs text-indigo-500">{t('learning.aiqAnalysis.perCurriculum')}</span>
          <p className="font-medium">{t('learning.aiqAnalysis.questionCount', { count: rule.perCurriculum })}</p>
        </div>
        <div>
          <span className="text-xs text-indigo-500">{t('learning.aiqAnalysis.totalTarget')}</span>
          <p className="font-medium">{t('learning.aiqAnalysis.questionCount', { count: rule.totalTarget })}</p>
        </div>
        <div className="col-span-2">
          <span className="text-xs text-indigo-500">{t('learning.aiqAnalysis.difficultyDistribution')}</span>
          <div className="flex flex-wrap gap-2 mt-1">
            {Object.entries(rule.byLevel).map(([level, count]) => (
              <DifficultyBadge key={level} level={Number(level)} count={count} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function CurriculumCard({ curriculum }: { curriculum: AiqCurriculumPoolInfo }) {
  const { t } = useTranslation('learner')
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex gap-3 items-center px-4 py-3 w-full text-left transition-colors hover:bg-gray-50"
      >
        {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center">
            <BookOpen className="flex-shrink-0 w-4 h-4 text-indigo-500" />
            <span className="text-sm font-medium text-gray-900 truncate">
              {curriculum.curriculumName ?? `${t('learning.aiqAnalysis.curriculum')} #${curriculum.curriculumId}`}
            </span>
            {curriculum.meetsRequirement ? (
              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-emerald-500" />
            ) : (
              <AlertTriangle className="flex-shrink-0 w-4 h-4 text-amber-500" />
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            <span className="text-xs text-gray-500">{t('learning.aiqAnalysis.questionCount', { count: curriculum.totalCount })}</span>
            {[1, 3, 5].map(level => {
              const count = curriculum.byDifficulty[level] ?? 0
              if (count === 0) return null
              return <DifficultyBadge key={level} level={level} count={count} />
            })}
          </div>
        </div>
      </button>

      {!curriculum.meetsRequirement && Object.keys(curriculum.shortfalls).length > 0 && (
        <div className="flex gap-2 items-center px-4 py-2 bg-amber-50 border-t border-amber-100">
          <AlertTriangle className="flex-shrink-0 w-3.5 h-3.5 text-amber-600" />
          <span className="text-xs text-amber-700">
            {t('learning.aiqAnalysis.shortage')}: {Object.entries(curriculum.shortfalls).map(([level, count]) => `Lv.${level} ${count}`).join(', ')}
          </span>
        </div>
      )}

      {expanded && curriculum.byLesson.length > 0 && (
        <div className="border-t border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500">
                <th className="px-4 py-2 font-medium text-left">{t('learning.aiqAnalysis.lesson')}</th>
                <th className="px-2 py-2 font-medium text-center w-14">{t('learning.aiqAnalysis.total')}</th>
                <th className="px-2 py-2 font-medium text-center w-14">Lv.1</th>
                <th className="px-2 py-2 font-medium text-center w-14">Lv.3</th>
                <th className="px-2 py-2 font-medium text-center w-14">Lv.5</th>
                <th className="px-2 py-2 font-medium text-left w-32">{t('learning.aiqAnalysis.distribution')}</th>
              </tr>
            </thead>
            <tbody>
              {curriculum.byLesson.map(lesson => (
                <tr key={lesson.lessonId} className="border-t border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700 truncate max-w-[200px]">
                    {lesson.lessonName ?? `${t('learning.aiqAnalysis.lesson')} #${lesson.lessonId}`}
                  </td>
                  <td className="px-2 py-2 font-medium text-center text-gray-900">{lesson.totalCount}</td>
                  <td className="px-2 py-2 text-center text-emerald-600">{lesson.byDifficulty[1] ?? '-'}</td>
                  <td className="px-2 py-2 text-center text-amber-600">{lesson.byDifficulty[3] ?? '-'}</td>
                  <td className="px-2 py-2 text-center text-rose-600">{lesson.byDifficulty[5] ?? '-'}</td>
                  <td className="px-2 py-2">
                    <DifficultyBar byDifficulty={lesson.byDifficulty} total={lesson.totalCount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && curriculum.byLesson.length === 0 && (
        <div className="px-4 py-3 text-xs text-gray-400 border-t border-gray-100">
          {t('learning.aiqAnalysis.noAiqQuizzes')}
        </div>
      )}
    </div>
  )
}

function SimulationResult({ data }: { data: AiqSimulateSelectionResponse }) {
  const { t } = useTranslation('learner')
  const [groupBy, setGroupBy] = useState<'curriculum' | 'difficulty'>('curriculum')

  const grouped = useMemo(() => {
    if (groupBy === 'curriculum') {
      const map = new Map<number, AiqSelectedQuizInfo[]>()
      for (const q of data.selectedQuizzes) {
        const list = map.get(q.curriculumId) ?? []
        list.push(q)
        map.set(q.curriculumId, list)
      }
      return map
    } else {
      const map = new Map<number, AiqSelectedQuizInfo[]>()
      for (const q of data.selectedQuizzes) {
        const list = map.get(q.difficultyLevel) ?? []
        list.push(q)
        map.set(q.difficultyLevel, list)
      }
      return map
    }
  }, [data.selectedQuizzes, groupBy])

  const curriculumNameMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of data.byCurriculum) {
      map.set(c.curriculumId, c.curriculumName ?? `${t('learning.aiqAnalysis.curriculum')} #${c.curriculumId}`)
    }
    return map
  }, [data.byCurriculum])

  return (
    <div className="space-y-3">
      <div className="flex gap-2 justify-between items-center">
        <div className="text-sm text-gray-600">
          {t('learning.aiqAnalysis.totalSelected', { count: data.totalSelected })}
        </div>
        <div className="flex gap-1 p-0.5 bg-gray-100 rounded-md">
          <button
            onClick={() => setGroupBy('curriculum')}
            className={cn('px-2 py-1 text-xs rounded', groupBy === 'curriculum' ? 'bg-white shadow-sm font-medium' : 'text-gray-500')}
          >
            {t('learning.aiqAnalysis.byCurriculum')}
          </button>
          <button
            onClick={() => setGroupBy('difficulty')}
            className={cn('px-2 py-1 text-xs rounded', groupBy === 'difficulty' ? 'bg-white shadow-sm font-medium' : 'text-gray-500')}
          >
            {t('learning.aiqAnalysis.byDifficulty')}
          </button>
        </div>
      </div>

      {data.byCurriculum.map(c => (
        <div key={c.curriculumId} className="flex flex-wrap gap-1.5 items-center text-xs">
          <span className="font-medium text-gray-700">{c.curriculumName ?? `${t('learning.aiqAnalysis.curriculum')} #${c.curriculumId}`}:</span>
          <span className="text-gray-500">{t('learning.aiqAnalysis.questionCount', { count: c.selectedCount })}</span>
          {Object.entries(c.byDifficulty).map(([level, count]) => (
            <DifficultyBadge key={level} level={Number(level)} count={count} />
          ))}
        </div>
      ))}

      {data.warnings.length > 0 && (
        <div className="space-y-1">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex gap-2 items-center px-3 py-1.5 bg-amber-50 rounded text-xs text-amber-700">
              <AlertTriangle className="flex-shrink-0 w-3.5 h-3.5" />
              {w}
            </div>
          ))}
        </div>
      )}

      <div className="overflow-y-auto max-h-[400px] rounded-lg border border-gray-200">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="text-gray-500">
              <th className="px-3 py-2 font-medium text-left w-8">#</th>
              <th className="px-3 py-2 font-medium text-left">Quiz ID</th>
              <th className="px-3 py-2 font-medium text-center w-14">{t('learning.aiqAnalysis.difficulty')}</th>
              <th className="px-3 py-2 font-medium text-center w-20">{t('learning.aiqAnalysis.type')}</th>
              <th className="px-3 py-2 font-medium text-left">{t('learning.aiqAnalysis.sourceLesson')}</th>
            </tr>
          </thead>
          <tbody>
            {groupBy === 'curriculum'
              ? Array.from(grouped.entries()).map(([key, quizzes]) => (
                  <GroupSection
                    key={key}
                    label={curriculumNameMap.get(key) ?? `${t('learning.aiqAnalysis.curriculum')} #${key}`}
                    quizzes={quizzes}
                  />
                ))
              : Array.from(grouped.entries())
                  .sort(([a], [b]) => a - b)
                  .map(([key, quizzes]) => (
                    <GroupSection
                      key={key}
                      label={DIFFICULTY_COLORS[key]?.label ?? `Lv.${key}`}
                      quizzes={quizzes}
                    />
                  ))
            }
          </tbody>
        </table>
      </div>
    </div>
  )
}

function GroupSection({ label, quizzes }: { label: string; quizzes: AiqSelectedQuizInfo[] }) {
  const { t } = useTranslation('learner')
  return (
    <>
      <tr className="bg-gray-50/80">
        <td colSpan={5} className="px-3 py-1.5 font-semibold text-gray-700">
          {label} ({t('learning.aiqAnalysis.questionCount', { count: quizzes.length })})
        </td>
      </tr>
      {quizzes.map((q, idx) => {
        const color = DIFFICULTY_COLORS[q.difficultyLevel]
        return (
          <tr key={q.quizId} className="border-t border-gray-50 hover:bg-gray-50">
            <td className="px-3 py-1.5 text-gray-400">{idx + 1}</td>
            <td className="px-3 py-1.5 font-mono text-gray-600">{q.quizId}</td>
            <td className="px-3 py-1.5 text-center">
              <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', color?.bg, color?.text)}>
                Lv.{q.difficultyLevel}
              </span>
            </td>
            <td className="px-3 py-1.5 text-center text-gray-500">{q.quizType ?? '-'}</td>
            <td className="px-3 py-1.5 text-gray-600 truncate max-w-[180px]">
              {q.lessonName ?? `${t('learning.aiqAnalysis.lesson')} #${q.lessonId}`}
            </td>
          </tr>
        )
      })}
    </>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function AiqPoolAnalysis({ productId }: { productId: number }) {
  const { t } = useTranslation('learner')
  const [activeTab, setActiveTab] = useState<'overview' | 'simulation'>('overview')

  const { data: poolAnalysis, isLoading: isLoadingPool } = useQuery({
    queryKey: ['aiqPoolAnalysis', productId],
    queryFn: async () => {
      const res = await aiqApi.getPoolAnalysis(productId)
      return res.data.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const {
    data: simulation,
    isLoading: isLoadingSimulation,
    refetch: refetchSimulation,
    isFetching: isFetchingSimulation,
  } = useQuery({
    queryKey: ['aiqSimulation', productId],
    queryFn: async () => {
      const res = await aiqApi.simulateSelection(productId)
      return res.data.data
    },
    enabled: activeTab === 'simulation',
    staleTime: 0,
    gcTime: 0,
  })

  if (isLoadingPool) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="inline-block w-6 h-6 rounded-full border-b-2 border-indigo-600 animate-spin" />
          <p className="mt-2 text-sm text-gray-500">{t('learning.aiqAnalysis.analyzingPool')}</p>
        </div>
      </div>
    )
  }

  if (!poolAnalysis) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-sm text-gray-500">{t('learning.aiqAnalysis.cannotLoadData')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab Header */}
      <div className="flex gap-4 items-center px-6 py-3 border-b border-gray-200 bg-gray-50/50">
        <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
          <button
            onClick={() => setActiveTab('overview')}
            className={cn(
              'flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all',
              activeTab === 'overview' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <BarChart3 className="w-3.5 h-3.5" />
            {t('learning.aiqAnalysis.poolOverview')}
          </button>
          <button
            onClick={() => setActiveTab('simulation')}
            className={cn(
              'flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all',
              activeTab === 'simulation' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            <Shuffle className="w-3.5 h-3.5" />
            {t('learning.aiqAnalysis.simulation')}
          </button>
        </div>

        {activeTab === 'simulation' && (
          <button
            onClick={() => refetchSimulation()}
            disabled={isFetchingSimulation}
            className="flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md transition-colors hover:bg-indigo-100 disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isFetchingSimulation && 'animate-spin')} />
            {t('learning.aiqAnalysis.redraw')}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1 p-6">
        {activeTab === 'overview' ? (
          <PoolOverview data={poolAnalysis} />
        ) : (
          isLoadingSimulation ? (
            <div className="flex justify-center items-center py-16">
              <div className="text-center">
                <div className="inline-block w-6 h-6 rounded-full border-b-2 border-indigo-600 animate-spin" />
                <p className="mt-2 text-sm text-gray-500">{t('learning.aiqAnalysis.runningSimulation')}</p>
              </div>
            </div>
          ) : simulation ? (
            <SimulationResult data={simulation} />
          ) : (
            <div className="flex justify-center items-center py-16">
              <p className="text-sm text-gray-500">{t('learning.aiqAnalysis.cannotLoadSimulation')}</p>
            </div>
          )
        )}
      </div>
    </div>
  )
}

function PoolOverview({ data }: { data: AiqPoolAnalysisResponse }) {
  const { t } = useTranslation('learner')
  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-xs text-gray-500">{t('learning.aiqAnalysis.totalAiqQuestions')}</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{data.totalQuizCount}</div>
          {data.uniqueQuizCount > 0 && data.totalQuizCount !== data.uniqueQuizCount && (
            <div className="mt-1 text-xs text-amber-600">
              {t('learning.aiqAnalysis.duplicates', { dup: data.totalQuizCount - data.uniqueQuizCount, unique: data.uniqueQuizCount })}
            </div>
          )}
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-xs text-gray-500">{t('learning.aiqAnalysis.curriculumCount')}</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{data.byCurriculum.length}</div>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="text-xs text-gray-500">{t('learning.aiqAnalysis.selectionTarget')}</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{data.selectionRule.totalTarget}</div>
        </div>
      </div>

      {/* Difficulty Distribution */}
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">{t('learning.aiqAnalysis.overallDifficultyDistribution')}</h3>
        <DifficultyBar byDifficulty={data.byDifficulty} total={data.totalQuizCount} />
        <div className="flex flex-wrap gap-2 mt-2">
          {[1, 2, 3, 4, 5].map(level => {
            const count = data.byDifficulty[level]
            if (!count) return null
            return <DifficultyBadge key={level} level={level} count={count} />
          })}
        </div>
      </div>

      {/* Selection Rule */}
      <SelectionRuleCard rule={data.selectionRule} />

      {/* Issues */}
      {data.issues.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-amber-800">{t('learning.aiqAnalysis.warnings')}</h3>
          {data.issues.map((issue, i) => (
            <div key={i} className="flex gap-2 items-start px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
              <AlertTriangle className="flex-shrink-0 mt-0.5 w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700">{issue}</span>
            </div>
          ))}
        </div>
      )}

      {/* Curriculum Cards */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">{t('learning.aiqAnalysis.curriculumDetails')}</h3>
        {data.byCurriculum.map(curriculum => (
          <CurriculumCard key={curriculum.curriculumId} curriculum={curriculum} />
        ))}
      </div>
    </div>
  )
}
